import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Flame, Star, Users, Building2, Loader2 } from 'lucide-react';

const LeaderboardWidget = ({ parentId, organizationId, compact = false }) => {
  const { api, user } = useAuth();
  const [familyLeaderboard, setFamilyLeaderboard] = useState([]);
  const [orgLeaderboard, setOrgLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('family');
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboards();
  }, [parentId, organizationId, period]);

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      // Fetch family leaderboard
      if (parentId) {
        const familyRes = await api.get(`/leaderboard/family/${parentId}?period=${period}`);
        setFamilyLeaderboard(familyRes.data.leaderboard || []);
      }
      
      // Fetch org leaderboard if user has an organization
      if (organizationId) {
        const orgRes = await api.get(`/leaderboard/organization/${organizationId}?period=${period}`);
        setOrgLeaderboard(orgRes.data.leaderboard || []);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-amber-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-700" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-slate-400">#{rank}</span>;
  };

  const getRankBgColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200';
    if (rank === 2) return 'bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
    return 'bg-white';
  };

  const activeLeaderboard = activeTab === 'family' ? familyLeaderboard : orgLeaderboard;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    // Show top 3 only
    const top3 = familyLeaderboard.slice(0, 3);
    return (
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-amber-500" />
            <span className="font-semibold text-slate-800">Family Leaderboard</span>
          </div>
          <div className="space-y-2">
            {top3.map((entry, index) => (
              <div key={entry.child_id} className="flex items-center gap-2">
                {getRankIcon(entry.rank)}
                <span className="flex-1 font-medium text-slate-700 truncate">{entry.name}</span>
                <span className="text-sm font-bold text-amber-600">{entry.xp_earned} XP</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="leaderboard-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-amber-500" />
            Leaderboard
          </CardTitle>
          {/* Period selector */}
          <div className="flex gap-1">
            {['week', 'month', 'all_time'].map((p) => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPeriod(p)}
                className={`text-xs ${period === p ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
              >
                {p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'All Time'}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Tab selector */}
        {organizationId && (
          <div className="flex gap-2 mb-4">
            <Button
              variant={activeTab === 'family' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('family')}
              className={activeTab === 'family' ? 'bg-teal-500 hover:bg-teal-600' : ''}
            >
              <Users className="w-4 h-4 mr-1" />
              Family
            </Button>
            <Button
              variant={activeTab === 'organization' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('organization')}
              className={activeTab === 'organization' ? 'bg-blue-500 hover:bg-blue-600' : ''}
            >
              <Building2 className="w-4 h-4 mr-1" />
              School
            </Button>
          </div>
        )}

        {/* Leaderboard list */}
        {activeLeaderboard.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">No leaderboard data yet</p>
            <p className="text-sm text-slate-400">Complete daily tasks to appear here!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeLeaderboard.map((entry, index) => (
              <motion.div
                key={entry.child_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center gap-3 p-3 rounded-xl border ${getRankBgColor(entry.rank)}`}
              >
                {/* Rank */}
                <div className="w-8 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                  entry.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-500' :
                  entry.rank === 2 ? 'bg-gradient-to-br from-slate-400 to-gray-500' :
                  entry.rank === 3 ? 'bg-gradient-to-br from-orange-400 to-amber-500' :
                  'bg-gradient-to-br from-teal-400 to-teal-500'
                }`}>
                  {entry.avatar}
                </div>
                
                {/* Name and stats */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{entry.name}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      {entry.total_xp} XP total
                    </span>
                    {entry.current_streak > 0 && (
                      <span className="flex items-center gap-1">
                        <Flame className="w-3 h-3 text-orange-500" />
                        {entry.current_streak}d streak
                      </span>
                    )}
                  </div>
                </div>
                
                {/* XP earned this period */}
                <div className="text-right">
                  <p className="font-bold text-lg text-amber-600">+{entry.xp_earned}</p>
                  <p className="text-xs text-slate-500">XP this {period === 'week' ? 'week' : period === 'month' ? 'month' : 'time'}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeaderboardWidget;
